// Localização: frontend/src/pages/Management/EquipamentoPage.js (VERSÃO 0.2.4 - SearchContext)

import React, { useState, useMemo, useEffect } from 'react';
import { useEquipamentos } from '../../hooks/useEquipamentos';
import { useContasPagar } from '../../hooks/useContasPagar';
import { useSearch } from '../../context/SearchContext';
import { useNotification } from '../../context/NotificationContext';
import PageTitle from '../../components/Layout/PageTitle';
import LoadingIndicator from '../../components/Common/LoadingIndicator';
import PrimaryButton from '../../components/Common/PrimaryButton';
import Modal from '../../components/UI/Modal';

// Importações de Conteúdo e Card
import EquipamentoFormContent from '../../components/Management/EquipamentoFormModal'; 
import VincularEquipamentoContent from '../../components/Management/VincularEquipamentoModal'; 
import EquipamentoCard from '../../components/Management/EquipamentoCard';
import FormContaPagar from '../../components/Financeiro/FormContaPagar';

// Ícones
import { Plus, BatteryCharging } from 'lucide-react'; 

// --- Componente Principal ---
const EquipamentoPage = () => {
    const { 
        equipamentos, 
        loading, 
        error, 
        canManage, 
        saveEquipamento, 
        deleteEquipamento,
        vincularEquipamento,
        desvincularEquipamento
    } = useEquipamentos();
    
    const { createContaPagar } = useContasPagar();
    const { success: showSuccess, error: showError } = useNotification();
    
    // Estados do Modal de Edição/Criação
    const [isFormModalOpen, setIsFormModalOpen] = useState(false);
    const [currentEquipamento, setCurrentEquipamento] = useState(null); // Dados para edição

    // Estados do Modal de Vínculo
    const [isVincularModalOpen, setIsVincularModalOpen] = useState(false);
    const [equipamentoToVincular, setEquipamentoToVincular] = useState(null);

    // Estados do Modal de Conta a Pagar
    const [isContaPagarModalOpen, setIsContaPagarModalOpen] = useState(false);
    const [equipamentoParaContaPagar, setEquipamentoParaContaPagar] = useState(null);
    const [isSavingContaPagar, setIsSavingContaPagar] = useState(false);

    // 🎯 USANDO SEARCHCONTEXT: Substituir estado local por contexto global
    const { searchTerm, setSearchPlaceholder } = useSearch();

    // Configurar o placeholder de busca para esta página
    useEffect(() => {
        setSearchPlaceholder('Buscar por nome, série, marca ou paciente...');
    }, [setSearchPlaceholder]);

    // 🎯 Lógica de Filtragem usando SearchContext
    const filteredEquipamentos = useMemo(() => {
        if (!searchTerm) {
            return equipamentos;
        }

        const lowerCaseSearch = searchTerm.toLowerCase();

        return equipamentos.filter(equipamento => 
            equipamento.nome_equipamento?.toLowerCase().includes(lowerCaseSearch) ||
            equipamento.numero_serie?.toLowerCase().includes(lowerCaseSearch) ||
            equipamento.marca?.toLowerCase().includes(lowerCaseSearch) ||
            equipamento.status?.toLowerCase().includes(lowerCaseSearch) ||
            (equipamento.paciente_vinculado_nome && equipamento.paciente_vinculado_nome.toLowerCase().includes(lowerCaseSearch))
        );
    }, [equipamentos, searchTerm]);


    // Handlers CRUD (mantidos)
    const handleNewClick = () => {
        setCurrentEquipamento(null);
        setIsFormModalOpen(true);
    };

    const handleEditClick = (equipamento) => {
        setCurrentEquipamento(equipamento);
        setIsFormModalOpen(true);
    };

    // Handler para quando o formulário de equipamento fecha
    const handleFormClose = (data) => {
        setIsFormModalOpen(false);
        // Se retornou dados com flag de criar conta a pagar, abrir modal de conta a pagar
        if (data?.criarContaPagar && data?.equipamento) {
            setEquipamentoParaContaPagar(data.equipamento);
            setIsContaPagarModalOpen(true);
        }
    };

    // Handler para salvar conta a pagar
    const handleSaveContaPagar = async (formData) => {
        setIsSavingContaPagar(true);
        try {
            await createContaPagar(formData);
            showSuccess('Conta a pagar criada com sucesso!');
            setIsContaPagarModalOpen(false);
            setEquipamentoParaContaPagar(null);
        } catch (error) {
            showError(`Erro ao criar conta a pagar: ${error.message}`);
        } finally {
            setIsSavingContaPagar(false);
        }
    };

    // Gerar descrição inicial baseada no equipamento
    const getDescricaoInicial = () => {
        if (!equipamentoParaContaPagar) return '';
        
        const partes = [`Equipamento: ${equipamentoParaContaPagar.nome_equipamento}`];
        if (equipamentoParaContaPagar.marca) {
            partes.push(`Marca: ${equipamentoParaContaPagar.marca}`);
        }
        if (equipamentoParaContaPagar.numero_serie) {
            partes.push(`Série: ${equipamentoParaContaPagar.numero_serie}`);
        }
        return partes.join(' - ');
    };

    const handleDelete = async (id, nome) => {
        if (window.confirm(`Tem certeza que deseja excluir o equipamento "${nome}"?`)) {
            const result = await deleteEquipamento(id);
            if (result.success) {
                showSuccess(result.message);
            } else {
                showError(`Erro: ${result.message}`);
            }
        }
    };
    
    // Handlers de Vínculo (mantidos)
    const handleVincularClick = (equipamento) => {
        setEquipamentoToVincular(equipamento);
        setIsVincularModalOpen(true);
    };

    const handleDesvincularClick = async (equipamento) => {
        if (!equipamento.vinculo_id) {
            showError("Erro: ID de vínculo não encontrado.");
            return;
        }
        if (window.confirm(`Tem certeza que deseja desvincular o equipamento "${equipamento.nome_equipamento}" do paciente "${equipamento.paciente_vinculado_nome}"?`)) {
            const result = await desvincularEquipamento(equipamento.vinculo_id);
            if (result.success) {
                showSuccess(result.message);
            } else {
                showError(`Erro: ${result.message}`);
            }
        }
    };

    if (loading && equipamentos.length === 0) {
        return <LoadingIndicator message="Carregando equipamentos..." />;
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 min-h-screen">
            <div className="max-w-7xl mx-auto">
                {/* Cabeçalho */}
                <PageTitle 
                title={`Gestão de Equipamentos (${equipamentos.length})`}
                subtitle="Gerencie equipamentos médicos e dispositivos da sua organização"
            >
                {canManage && (
                    <PrimaryButton onClick={handleNewClick} className="flex items-center bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto">
                        <Plus className="w-5 h-5 mr-2" /> 
                        Novo Equipamento
                    </PrimaryButton>
                )}
            </PageTitle>

            {/* 🎯 Barra de Pesquisa removida - Agora está no HeaderGestão */}

            {error && (
                <div className="p-4 mb-4 text-sm text-red-700 bg-red-100 rounded-lg" role="alert">
                    {error}
                </div>
            )}
            
            {loading && equipamentos.length > 0 && <p className="text-emerald-600 text-center py-2">Atualizando lista...</p>}

            {/* Lista de Equipamentos (Layout de Grid - Responsivo: 2 colunas no mobile, 4 colunas no desktop) */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6">
                {filteredEquipamentos.length > 0 ? (
                    filteredEquipamentos.map(equipamento => (
                        <EquipamentoCard 
                            key={equipamento.id} 
                            equipamento={equipamento} 
                            canManage={canManage}
                            onEdit={handleEditClick}
                            onDelete={handleDelete}
                            onVincular={handleVincularClick}
                            onDesvincular={handleDesvincularClick}
                        />
                    ))
                ) : (
                    <div className="md:col-span-4 col-span-2"> {/* Para centralizar o "nenhum resultado" */}
                        <p className="text-gray-500 text-center p-8 border border-dashed rounded-xl bg-white shadow-lg">
                            {searchTerm 
                                ? `Nenhum equipamento encontrado para "${searchTerm}".` 
                                : 'Nenhum equipamento registrado na sua organização.'}
                        </p>
                    </div>
                )}
            </div>
            
            {/* Modal de Criação/Edição (Mantido) */}
            <Modal
                isOpen={isFormModalOpen}
                onClose={() => setIsFormModalOpen(false)}
                title={currentEquipamento ? 'Editar Equipamento' : 'Novo Equipamento'}
                size="lg"
            >
                <EquipamentoFormContent
                    onSubmit={saveEquipamento}
                    onClose={handleFormClose}
                    initialData={currentEquipamento || {}}
                />
            </Modal>

            {/* Modal de Vínculo (Mantido) */}
            <Modal
                isOpen={isVincularModalOpen}
                onClose={() => setIsVincularModalOpen(false)}
                title={`Vincular Equipamento: ${equipamentoToVincular?.nome_equipamento || 'Equipamento'}`}
                size="md" 
            >
                <VincularEquipamentoContent
                    onSubmit={vincularEquipamento}
                    onClose={() => setIsVincularModalOpen(false)}
                    equipamento={equipamentoToVincular}
                />
            </Modal>

            {/* Modal de Conta a Pagar (Novo) */}
            <Modal
                isOpen={isContaPagarModalOpen}
                onClose={() => {
                    setIsContaPagarModalOpen(false);
                    setEquipamentoParaContaPagar(null);
                }}
                title="Criar Conta a Pagar - Equipamento"
                size="lg"
            >
                <FormContaPagar
                    conta={null}
                    onSave={handleSaveContaPagar}
                    onCancel={() => {
                        setIsContaPagarModalOpen(false);
                        setEquipamentoParaContaPagar(null);
                    }}
                    isLoading={isSavingContaPagar}
                    descricaoInicial={getDescricaoInicial()}
                />
            </Modal>
            </div>
        </div>
    );
};

export default EquipamentoPage;